import {
  Collapse,
  createStyles,
  Divider,
  Drawer,
  Grid,
  IconButton,
  makeStyles,
  Radio,
  Theme,
  Tooltip,
  Typography,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ArrowForward, Cancel, CheckCircle, KeyboardArrowDown, KeyboardArrowUp, WatchLater } from '@material-ui/icons';
import { statusFormularioDescriptionsAditamento } from 'common/genericDescriptions';
import { AditamentoStatus, GrupoAcesso, IAditamento, InstrumentoFinanceiro, FuncaoParticipante } from 'common/genericTypes';
import { Button, LgSpace, MdSpace, SmSpace, TextButton } from 'm2m.components';
import React, { useCallback, useState } from 'react';
import { AditamentoRevisionModal, AditamentoRevisionData } from '../../../components/aditamentoModal/aditamentoRevisionModal';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { RootState } from 'store';
import { AditamentoContext, aditamentoInitialState, AditamentosContextType } from '.';

const useStyles = (status: AditamentoStatus) =>
  makeStyles((theme: Theme) => {
    const colors: { [key: string]: string } = {
      [AditamentoStatus.EmAnalise]: theme.palette.warning.main,
      [AditamentoStatus.Aprovado]: theme.palette.primary.main,
      [AditamentoStatus.Reprovado]: theme.palette.error.main,
    };
    return createStyles({
      icon: {
        color: colors[status] ?? theme.palette.grey,
        marginRight: 10,
      },
      wrapTxt: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      textButton: {
        padding: '0 0 0 0',
        marginRight: '10px',
        borderBottom: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 0,
      },
    });
  });

interface AditamentoListItemProps {
  aditamento: IAditamento;
}

export const statusFormularioAditamento: { [key: string]: string } = {
  [AditamentoStatus.EmAnalise]: 'Em Análise',
  [AditamentoStatus.Reprovado]: 'Reprovado',
  [AditamentoStatus.Aprovado]: 'Aprovado',
};

type Props = AditamentoListItemProps;

export const AditamentoListItem: React.FC<Props> = (props) => {
  const { aditamento } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const { selected, setSelected, setChecked, isUsuarioOperacoes } = React.useContext(
    AditamentoContext
  ) as AditamentosContextType;

  const classes = useStyles(aditamento.statusAditamentoId)();
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [openJustificativa, setOpenJustificativa] = useState<boolean>(false);
  const [openRevisionModal, setOpenRevisionModal] = useState<boolean>(false);
  const [revisionData, setRevisionData] = useState<AditamentoRevisionData | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const getStatusIcon = (status: AditamentoStatus) => {
    switch (status) {
      case AditamentoStatus.EmAnalise:
        return <WatchLater fontSize="small" className={classes.icon} />;
      case AditamentoStatus.Aprovado:
        return <CheckCircle fontSize="small" className={classes.icon} />;
      case AditamentoStatus.Reprovado:
        return <Cancel fontSize="small" className={classes.icon} />;
      default:
        return <></>;
    }
  };
  const getAditamentoDocumento = useSelector((state: RootState) => state.aditamentosApi.getAditamentoDocument);
  const getGUID = useSelector((state: RootState) => state.aditamentosApi.getGUIDInstrumentoFinanceiro);
  const getAditamentoCertificadoRecebiveis = useSelector((state: RootState) => state.certificadoRecebiveisApi.getAditamentoCertificadoRecebiveis);
  const getAditamentoCdca = useSelector((state: RootState) => state.cdcaApi.getAditamentoCdca);
  const getAditamentoCra = useSelector((state: RootState) => state.craApi.getAditamentoCra);
  const getAditamentoCotasDeFundo = useSelector((state: RootState) => state.cotasDeFundoApi.getAditamentoCotasDeFundo);
  const getAditamentoCri = useSelector((state: RootState) => state.criApi.getAditamentoCri);
  const getAditamentoDebenture = useSelector((state: RootState) => state.debentureApi.getAditamentoDebenture);
  const getAditamentoNotaComercial = useSelector((state: RootState) => state.notaComercialApi.getAditamentoNotaComercial);
  const isUsuarioAgenteFiduciario = useSelector((state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.AgenteFiduciario);
  const isUsuarioEscriturador = useSelector((state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.Escriturador);
  const solicitanteFuncao = (aditamento as any)?.solicitanteFuncao as string | undefined;
  const solicitanteIsAF = solicitanteFuncao === 'AGENTE_FIDUCIARIO' || solicitanteFuncao === FuncaoParticipante.AgenteFiduciario;
  const solicitanteIsEscriturador = solicitanteFuncao === 'ESCRITURADOR' || solicitanteFuncao === FuncaoParticipante.Escriturador;

  const canSelect = Boolean(
    isUsuarioOperacoes ||
      (isUsuarioAgenteFiduciario && solicitanteIsEscriturador) ||
      (isUsuarioEscriturador && solicitanteIsAF)
  );

  const getDocumento = async () => {
    try {
      const resp = await getAditamentoDocumento(dispatch, aditamento.aditamentoGuid);

      window.open(resp, '_blank');
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange = (event: boolean) => {
    if (event === true) {
      setChecked(event);
      setSelected(aditamento);
    } else {
      setChecked(event);
      setSelected(aditamentoInitialState);
    }
  };

  const handleDetailsEmissao = async () => {
    const instrumentoFinanceiroGuid = await getGUID(dispatch, aditamento.instrumentoFinanceiroGuid);
    switch (aditamento.tipoInstrumentoFinanceiro) {
      case InstrumentoFinanceiro.CertificadoRecebiveis:
        history.push(`instrumentos/gerenciar/certificados-recebiveis/${instrumentoFinanceiroGuid}`);
        break;
      case InstrumentoFinanceiro.Cra:
        history.push(`instrumentos/gerenciar/cras/${instrumentoFinanceiroGuid}`);
        break;
      case InstrumentoFinanceiro.Cdca:
        history.push(`instrumentos/gerenciar/cdcas/${instrumentoFinanceiroGuid}`);
        break;
      case InstrumentoFinanceiro.CotasDeFundo:
        history.push(`instrumentos/gerenciar/cotas-de-fundos/${instrumentoFinanceiroGuid}`);
        break;
      case InstrumentoFinanceiro.Cri:
        history.push(`instrumentos/gerenciar/cris/${instrumentoFinanceiroGuid}`);
        break;
      case InstrumentoFinanceiro.Debenture:
        history.push(`instrumentos/gerenciar/debentures/${instrumentoFinanceiroGuid}`);
        break;
      case InstrumentoFinanceiro.NotaComercial:
        history.push(`instrumentos/gerenciar/notas-comerciais/${instrumentoFinanceiroGuid}`);
        break;
    }
  };

  const handleOpenRevisionModal = async () => {
    let response: AditamentoRevisionData | null = null;

    try {
      switch (aditamento.tipoInstrumentoFinanceiro) {
        case InstrumentoFinanceiro.CertificadoRecebiveis:
          response = await getAditamentoCertificadoRecebiveis(dispatch, aditamento.id.toString());
          break;
        case InstrumentoFinanceiro.Cra:
          response = await getAditamentoCra(dispatch, aditamento.id.toString());
          break;
        case InstrumentoFinanceiro.Cdca:
          response = await getAditamentoCdca(dispatch, aditamento.id.toString());
          break;
        case InstrumentoFinanceiro.CotasDeFundo:
          response = await getAditamentoCotasDeFundo(dispatch, aditamento.id.toString());
          break;
        case InstrumentoFinanceiro.Cri:
          response = await getAditamentoCri(dispatch, aditamento.id.toString());
          break;
        case InstrumentoFinanceiro.Debenture:
          response = await getAditamentoDebenture(dispatch, aditamento.id.toString());
          break;
        case InstrumentoFinanceiro.NotaComercial:
          response = await getAditamentoNotaComercial(dispatch, aditamento.id.toString());
          break;
      }
    } catch (error) {
      console.log(error);
    }

    setRevisionData(response);
    setOpenRevisionModal(true);
  };

  const handleCloseRevisionModal = (result?: { success: boolean; message: string }) => {
    setOpenRevisionModal(false);
    setRevisionData(null);

    if (result) {
      setSnackbarMessage(result.message);
      setSnackbarSeverity(result.success ? 'success' : 'error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = useCallback((event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  }, []);

  return (
    <div style={{ border: '1px solid #E5E5E5', borderRadius: 8, marginBottom: 10 }}>
      <div style={{ padding: 10 }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item sm={4}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {(isUsuarioOperacoes || isUsuarioAgenteFiduciario || isUsuarioEscriturador) && (
                <Radio
                  checked={aditamento.id === selected.id}
                  onChange={(event) => {
                    handleChange(event.target.checked);
                  }}
                  value={aditamento}
                  color="primary"
                  disabled={
                    aditamento.statusAditamentoId === 2 ||
                    aditamento.statusAditamentoId === 3 ||
                    !canSelect
                  }
                />
              )}
              <Tooltip
                title={statusFormularioAditamento[aditamento.statusAditamentoId]}
                aria-label={statusFormularioAditamento[aditamento.statusAditamentoId]}
              >
                {getStatusIcon(aditamento.statusAditamentoId)}
              </Tooltip>
              <Typography variant="body2" className={classes.wrapTxt}>
                {aditamento.codigoInstrumentoFinanceiro}
              </Typography>
            </div>
          </Grid>

          <Grid item sm={4} onClick={() => setOpenDetails(!openDetails)}>
            <Typography className={classes.wrapTxt} variant="body2">
              {aditamento.tipoInstrumentoFinanceiro}
            </Typography>
          </Grid>
          <Grid item sm={2} onClick={() => setOpenDetails(!openDetails)}>
            <Grid container justifyContent={'space-between'}>
              <Typography className={classes.wrapTxt} variant="body2">
                {aditamento.emissorRazaoSocial}
              </Typography>
            </Grid>
          </Grid>
          <Grid sm={2} container justifyContent={'flex-end'}>
            {/* <Grid> */}
            {aditamento.justificativa && (
              <TextButton
                className={classes.textButton}
                onClick={() => setOpenJustificativa(true)}
              >{`Ver Justificativa`}</TextButton>
            )}
            <IconButton aria-label="expand row" size="small" onClick={() => setOpenDetails(!openDetails)}>
              {openDetails ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
            {/* </Grid> */}
          </Grid>
        </Grid>
      </div>
      <Collapse in={openDetails} timeout="auto" unmountOnExit style={{ backgroundColor: '#F6F6F9', padding: 20 }}>
        <Grid container spacing={2}>
          <Grid item sm={4}>
            <Typography variant="body1">{'Número do aditamento:'}</Typography>
            <Typography variant="body2">{aditamento.aditamentoCount}</Typography>
          </Grid>
          <Grid item sm={4}>
            <Typography variant="body1">{'Razão Social Emissor:'}</Typography>
            <Typography variant="body2">{aditamento.emissorRazaoSocial}</Typography>
          </Grid>
          <Grid item sm={3}></Grid>
        </Grid>
        <SmSpace />
        <Grid container spacing={2}>
          <Grid item sm={4}>
            <Typography variant="body1">{'Data do Aditamento:'}</Typography>
            <Typography variant="body2">{new Date(aditamento.dataDeCriacao).toLocaleDateString('en-GB')}</Typography>
          </Grid>

          <Grid item sm={4}>
            <Typography variant="body1">{'Status:'}</Typography>
            <Typography variant="body2">
              {statusFormularioDescriptionsAditamento[aditamento.statusAditamentoId]}
            </Typography>
          </Grid>
        </Grid>

        <Grid container justifyContent={'flex-end'}>
          <Grid item sm={2} style={{ textAlign: 'right' }}>
            <TextButton onClick={handleOpenRevisionModal}>
              Visualizar alterações do Aditamento
              <ArrowForward style={{ marginLeft: 10 }} fontSize={'small'} />
            </TextButton>
          </Grid>
        </Grid>

        <Grid container justifyContent={'flex-end'}>
          <Grid item sm={2} style={{ textAlign: 'right' }}>
            <TextButton onClick={handleDetailsEmissao}>
              Mais Detalhes da Emissão
              <ArrowForward style={{ marginLeft: 10 }} fontSize={'small'} />
            </TextButton>
          </Grid>
        </Grid>

        <Grid container justifyContent={'flex-end'}>
          <Grid item sm={2} style={{ textAlign: 'right' }}>
            <TextButton onClick={getDocumento}>
              Visualizar termo de aditamento
              <ArrowForward style={{ marginLeft: 10 }} fontSize={'small'} />
            </TextButton>
          </Grid>
        </Grid>
      </Collapse>
      <JustificativaDrawer
        opened={openJustificativa}
        onClose={() => setOpenJustificativa(false)}
        textContents={aditamento.justificativa}
      />
      <AditamentoRevisionModal
        open={openRevisionModal}
        onClose={handleCloseRevisionModal}
        data={revisionData}
        onSave={_ => { }}
        allowEdit={isUsuarioOperacoes}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export interface JustificativaDrawerProps {
  opened: boolean;
  onClose(): void;
  textContents?: string;
}

export const JustificativaDrawer: React.FC<JustificativaDrawerProps> = (props) => {
  const { opened, onClose, textContents } = props;

  return (
    <Drawer anchor="right" open={opened} onClose={onClose} PaperProps={{ style: { padding: '40px', width: 520 } }}>
      <Grid item xl={12}>
        <Typography variant="h3">Justificativa</Typography>
        <SmSpace />
        <Divider variant="fullWidth" />
        <MdSpace />
      </Grid>
      <Grid container>
        <Grid item xl={12}>
          <Typography variant="body1">{textContents}</Typography>
        </Grid>
        <LgSpace />
      </Grid>
      <Grid item xl={12}>
        <Button variant="outlined" fullWidth onClick={onClose}>
          {'Fechar'}
        </Button>
      </Grid>
    </Drawer>
  );
};
